import type { Tokens } from './tokens'

/**
 * Structural type matching what MUI's createTheme accepts. Declared locally
 * so @commerce/tokens stays free of a MUI dependency — packages/web passes
 * this straight into createTheme.
 */
export interface MuiThemeInput {
  palette: {
    mode: 'light' | 'dark'
    primary: { main: string; contrastText: string }
    secondary: { main: string; contrastText: string }
    error: { main: string }
    warning: { main: string }
    info: { main: string }
    success: { main: string }
    background: { paper: string; default: string }
    text: { primary: string; secondary: string; disabled: string }
  }
  shape: { borderRadius: number }
  spacing: number
  typography: { fontFamily: string; fontSize: number }
}

/**
 * `logoUrl` is deliberately absent from the output: it is an asset the layout
 * renders, not a theme value, so it stays on `Tokens` for the shell to read.
 */
export const toMuiTheme = (t: Tokens): MuiThemeInput => ({
  palette: {
    mode: t.mode,
    primary: { main: t.color.primary, contrastText: t.color.primaryContrast },
    secondary: { main: t.color.secondary, contrastText: t.color.secondaryContrast },
    error: { main: t.color.error },
    warning: { main: t.color.warning },
    info: { main: t.color.info },
    success: { main: t.color.success },
    background: { paper: t.color.surface, default: t.color.background },
    text: {
      primary: t.color.textPrimary,
      secondary: t.color.textSecondary,
      disabled: t.color.textDisabled,
    },
  },
  shape: { borderRadius: t.radius.md },
  spacing: t.spacing.unit,
  // 14 is MUI's own default base size; `scale` is a multiplier so a client can
  // go bigger without having to know that number.
  typography: { fontFamily: t.font.family, fontSize: 14 * t.font.scale },
})
